import React, { useState } from "react";
import { motion } from "framer-motion";

import Notify from "../../utils/Notify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./FileUploadView.module.scss";

import StorageHelper from "../../utils/StorageHelper";

import checkmark from "../../assets/images/checkmark.svg";
import errorIcon from "../../assets/images/error.svg";

/**
 * Form for the user to upload their floor-plan file
 * (Only 1 file allowed)
 * */
const STATE = {
  idle: "idle",
  uploading: "uploading",
  success: "success",
  error: "error",
};

const MAX_FILE_SIZE_MB = 50;

function FileUploadView({ onFileUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState(STATE.idle);
  const [progress, setProgress] = useState(0); // Track progress

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDragOver(false);
    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      if (files.length === 1) {
        const file = files[0];
        uploadFile(file, onFileUploaded);
      } else {
        Notify.oops("Max 1 file allowed for this demo - please try again");
      }
    } else {
      Notify.oops("No file detected - please try again");
    }
  };

  const handleFileInputClick = async () => {
    // Detect if the user is on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (window.showOpenFilePicker && !isMobile) {
      // Desktop browser with support for file picker
      const pickerOpts = {
        types: [
          {
            accept: {
              "application/*": [".dwg", ".dxf", ".pdf"],
            },
          },
        ],
        excludeAcceptAllOption: true,
        multiple: false,
      };
      try {
        let [fileHandle] = await window.showOpenFilePicker(pickerOpts);
        const file = await fileHandle.getFile();
        uploadFile(file, onFileUploaded);
      } catch (err) {
        console.error(err);
      }
    } else {
      // Fallback for mobile or unsupported browsers (input element)
      const input = document.createElement("input");
      input.type = "file";
      if (!isMobile) {
        input.accept = ".dwg,.dxf,.pdf";
      }
      input.onchange = (event) => {
        const file = event.target.files[0];
        if (file) {
          uploadFile(file, onFileUploaded);
        }
      };
      input.click();
    }
  };

  const uploadFile = async (file, onFileUploaded) => {
    const name = file.name;
    const fileSizeMB = file.size / (1024 * 1024);

    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      Notify.oops(
        "File size exceeds the 50MB limit. Please upload a smaller file."
      );
      setStatus(STATE.error);
      return;
    }
    if (
      !name.toLowerCase().endsWith(".dwg") &&
      !name.toLowerCase().endsWith(".dxf")
    ) {
      Notify.oops(
        "This demo only supports DWG or DXF files. Please try again."
      );
      return;
    }

    setStatus(STATE.uploading);
    setProgress(0);

    await StorageHelper.upload(
      file,
      () => {
        setStatus(STATE.success);
        Notify.yay("File uploaded successfully");
        if (onFileUploaded) onFileUploaded(file);
      },
      (error) => {
        setStatus(STATE.error);
        console.error(error);
        Notify.hmm("Failed to upload file.");
      },
      (progress) => {
        setProgress(progress);
      }
    );
  };

  const reset = () => {
    setDragOver(false);
    setStatus(STATE.idle);
    setProgress(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <div className={styles.borderedContainer}>
        <div className={`${styles.blurEffect} `}></div>
        <div className={styles.dragAndDropContainer}>
          {status === STATE.idle && (
            <>
              <div
                className={styles.initialState}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={dragOver ? dropZoneActiveStyles : dropZoneStyles}
              >
                {dragOver ? (
                  <p>Release to drop</p>
                ) : (
                  <>
                    <p className={styles.mainParagraph}>
                      <button
                        className="text-button underline"
                        onClick={handleFileInputClick}
                      >
                        Click here
                      </button>{" "}
                      or drag & drop <br /> to upload your floor-plan
                    </p>
                    <div className={styles.supportedFormat}>
                      <h6>Supported Formats&nbsp;</h6>
                      <p>DWG, DXF</p>
                      <p>(Less than 50 MB)</p>
                    </div>
                  </>
                )}
              </div>
              <p className={styles.sampleMap}>
                If you don’t have a map, <br />
                <a
                  href="https://pointrapps.blob.core.windows.net/delivery-tools/websitemapscaledemo/Office_CAD.dwg"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  click here for a sample map.
                </a>
              </p>
            </>
          )}
          {status === STATE.uploading && (
            <div className={styles.uploadingState}>
              <div className={styles.statusText}>Uploading...</div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className={styles.percentageText}>{progress}%</div>
            </div>
          )}
          {status === STATE.success && (
            <div className={styles.successState}>
              <img src={checkmark} alt="checkmark" />
              <p className={styles.statusText}>
                Your map was uploaded succesfully!
              </p>
            </div>
          )}
          {status === STATE.error && (
            <>
              <div className={styles.errorState}>
                <img src={errorIcon} alt="error" />
                <p className={styles.statusText}>
                  There was an error uploading your map!
                </p>
              </div>
              <button
                className={`secondary-button ${styles.tryAgainButton}`}
                onClick={reset}
              >
                Try Again
              </button>
            </>
          )}
        </div>

        <div id={styles.leftGradient} className={styles.gradient}></div>
        <div id={styles.rightGradient} className={styles.gradient}></div>
      </div>
      <ToastContainer />
    </motion.div>
  );
}

const dropZoneStyles = {
  border: "2px dashed gray",
  display: "flex",
  paddingBottom: "26px",
  alignItems: "center",
  flexDirection: "column",
  backgroundColor: "#f9f9f9",
};

const dropZoneActiveStyles = {
  ...dropZoneStyles,
  backgroundColor: "#d1e7dd",
};

export default FileUploadView;
